import { useEffect, useMemo, useState } from 'react';
import { StanButton, StanCard, StanFilters, StanTable } from '@/components';
import { useCourseFans } from '@/queries/useCourses';
import { createIconElement } from '@/lib';
import { useCourseFansTableColumns } from '../../hooks/useCourseFansTableColumns';
import { CourseFilterField, CourseUsersTableProps } from '../../types/courseAnalytics.types';
import { CourseEmailEditor } from './CourseEmailEditor';
import IcMail from '@/assets/icons/ic-mail.svg';
import IcFilter from '@/assets/icons/ic-filter-funnel.svg';
import { getCourseAnalyticsFilters } from '../../logic/course.logic';
import { useQueryFilters } from '@/hooks/useQueryFilters';

const PAGE_SIZE = 10;
const mailIcon = createIconElement(IcMail, { className: 'size-5' });
const filterIcon = createIconElement(IcFilter, { className: 'size-5' });

export function CourseUsersTable({ courseId, analytics }: CourseUsersTableProps) {
    const [showEmailOptions, setShowEmailOptions] = useState(false);
    const [showMobileFilters, setShowMobileFilters] = useState(false);
    const [selectedModuleId, setSelectedModuleId] = useState<string>();

    const filters = useMemo(
        () => getCourseAnalyticsFilters(analytics, selectedModuleId),
        [analytics, selectedModuleId]
    );

    const { filterValues, handleFilterChange, handleResetFilters, backendFilterValues } = useQueryFilters({ filters });
    const currentModuleId = useMemo(
        () => filterValues.find(f => f.id === CourseFilterField.MODULE_ID)?.value,
        [filterValues]
    );

    useEffect(() => {
        if (currentModuleId) {
            setSelectedModuleId(String(currentModuleId));
        } else {
            setSelectedModuleId(undefined);
        }
    }, [currentModuleId]);

    const { data: fans, isFetching, refetch } = useCourseFans(courseId, backendFilterValues);

    const [currentPage, setCurrentPage] = useState(1);

    const tableData = useMemo(
        () =>
            fans?.map(fan => {
                return {
                    ...fan,
                    percentCompleteString: `${fan.percent_complete}%`,
                    dropdownMenu: null,
                };
            }) ?? [],
        [fans]
    );

    const totalPages = Math.ceil(tableData.length / PAGE_SIZE);
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    const pageData = tableData.slice(startIndex, endIndex);

    const goToPage = (page: number) => {
        setCurrentPage(Math.max(1, Math.min(page, totalPages)));
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [fans, showEmailOptions]);

    const columns = useCourseFansTableColumns({ showEmailOptions, courseId, onRefresh: refetch });

    if (showEmailOptions) {
        return (
            <CourseEmailEditor
                courseId={courseId}
                filteredUsersCount={tableData.length}
                totalUsersCount={analytics?.total_enrollment || 0}
                pendingFanIds={tableData.map(fan => fan.userFanId)}
                hasActiveFilters={filterValues.length > 0}
                onCancel={() => setShowEmailOptions(false)}
                onEmailSent={() => setShowEmailOptions(false)}
            />
        );
    }

    return (
        <StanCard>
            <div className='mb-2 flex w-full flex-wrap items-start justify-between gap-2'>
                <h3 className='text-dark pb-5'>Users</h3>
                <div className='flex items-center gap-2'>
                    <div className='md:hidden'>
                        <StanButton
                            name='course-analytics-filter-toggle-button'
                            color='secondary'
                            size='sm'
                            onClick={() => setShowMobileFilters(!showMobileFilters)}
                            icon={filterIcon}
                            className='!-mt-2'
                        >
                            Filters
                        </StanButton>
                    </div>
                    <StanButton
                        name='course-analytics-send-email-button'
                        color='secondary'
                        size='sm'
                        disabled={showEmailOptions || tableData.length === 0}
                        onClick={() => setShowEmailOptions(true)}
                        icon={mailIcon}
                        className='!-mt-2'
                    >
                        Send Email
                    </StanButton>
                </div>
            </div>
            <div className={`mb-4 ${showMobileFilters ? 'block' : 'hidden'} md:block`}>
                <StanFilters
                    filters={filters}
                    filterValues={filterValues}
                    onFilterChanged={handleFilterChange}
                    onResetFilters={handleResetFilters}
                />
            </div>
            <StanTable
                columns={columns}
                data={pageData}
                isLoading={isFetching}
                page={currentPage}
                totalPages={totalPages}
                onPageChange={goToPage}
                emptyHeader='No users enrolled'
                emptySubheader='Users will appear here once they enroll in the course'
            />
        </StanCard>
    );
}
